const LOAD_MORE_SELECTOR = '.il-load-more';
const SEARCH_FORM_SELECTOR = '.il-posts__header .il-form';
const SEARCH_DEBOUNCE_MS = 1200;

const searchTimers = new WeakMap();
const searchControllers = new WeakMap();

const getPostsContainer = (element) => element.closest('.il-posts');

const getLoadMoreButton = (postsContainer) => postsContainer?.querySelector(LOAD_MORE_SELECTOR) || null;

const createRequestData = (button, page, searchTerm = '') => {
	const formData = new FormData();

	formData.append('action', 'il_load_more_posts');
	formData.append('nonce', window.sendToJs?.ajaxnonce || '');
	formData.append('page', String(page));
	formData.append('max', button.dataset.max || '1');
	formData.append('perPage', button.dataset.perpage || '0');
	formData.append('postType', button.dataset.type || 'post');
	formData.append('tax', button.dataset.tax || '');
	formData.append('term', button.dataset.term || '');
	formData.append('customPostMessage', button.dataset.customMessage || '');
	formData.append('emptyPostsMessage', button.dataset.emptyMessage || '');
	formData.append('search', searchTerm);

	return formData;
};

const getWrapElement = (button) => {
	const wrapClass = button.dataset.wrap;
	const postsContainer = getPostsContainer(button);

	if (!wrapClass) {
		return null;
	}

	if (postsContainer) {
		const scopedWrap = postsContainer.querySelector(`.${CSS.escape(wrapClass)}`);

		if (scopedWrap) {
			return scopedWrap;
		}
	}

	return document.querySelector(`.${CSS.escape(wrapClass)}`);
};

const getFooterElement = (postsContainer) => postsContainer?.querySelector('.il-posts__footer') || null;

const appendPostsMarkup = (wrap, html) => {
	const template = document.createElement('template');
	template.innerHTML = html.trim();
	wrap.append(template.content);
};

const replacePostsMarkup = (wrap, html) => {
	wrap.innerHTML = html;
};

const syncButtonContentState = (button, isLoading) => {
	const spinner = button.querySelector('.il-button__spinner');

	if (spinner) {
		spinner.style.display = isLoading ? 'inline-block' : 'none';
	}
};

const syncFormContentState = (form, isLoading) => {
	const spinner = form.querySelector('.il-form__spinner');

	if (spinner) {
		spinner.style.display = isLoading ? 'inline-block' : 'none';
	}
};

const setButtonState = (button, isLoading) => {
	button.disabled = isLoading;
	button.classList.toggle('is-loading', isLoading);
	button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
	button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
	syncButtonContentState(button, isLoading);
};

const setFormState = (form, isLoading) => {
	form.classList.toggle('is-loading', isLoading);
	form.setAttribute('aria-busy', isLoading ? 'true' : 'false');
	syncFormContentState(form, isLoading);
};

const hideButton = (button) => {
	const wrapper = button.closest('.il-posts__button');

	if (wrapper) {
		wrapper.hidden = true;
		return;
	}

	button.hidden = true;
};

const showButton = (button) => {
	const wrapper = button.closest('.il-posts__button');

	if (wrapper) {
		wrapper.hidden = false;
	}

	button.hidden = false;
};

const updateFooter = (footer, footerText) => {
	if (footer && typeof footerText === 'string') {
		footer.textContent = footerText;
	}
};

const updateButtonPagination = (button, data, searchTerm) => {
	button.dataset.page = String(data?.nextPage || 2);
	button.dataset.max = String(data?.max || 1);
	button.dataset.search = searchTerm;

	if (data?.hasMore) {
		showButton(button);
		return;
	}

	hideButton(button);
};

const requestPosts = async (button, page, searchTerm = '', signal) => {
	const response = await fetch(window.sendToJs?.ajaxurl || '', {
		method: 'POST',
		credentials: 'same-origin',
		body: createRequestData(button, page, searchTerm),
		signal,
	});

	const payload = await response.json();

	if (!response.ok || !payload?.success) {
		throw new Error(payload?.data?.message || 'Unable to load posts.');
	}

	return payload.data;
};

const runSearch = async (form) => {
	const postsContainer = getPostsContainer(form);
	const button = getLoadMoreButton(postsContainer);
	const wrap = button ? getWrapElement(button) : postsContainer?.querySelector('.il-ajax') || null;
	const footer = getFooterElement(postsContainer);
	const searchField = form.querySelector('[name="s"]');
	const searchTerm = searchField instanceof HTMLInputElement ? searchField.value.trim() : '';
	const previousController = searchControllers.get(form);
	const controller = new AbortController();

	if (!button || !wrap) {
		return;
	}

	if (previousController) {
		previousController.abort();
	}

	searchControllers.set(form, controller);
	setFormState(form, true);

	try {
		const data = await requestPosts(button, 1, searchTerm, controller.signal);

		replacePostsMarkup(wrap, data?.html || '');
		updateFooter(footer, data?.footerText);
		updateButtonPagination(button, data, searchTerm);
	} catch (error) {
		if (error instanceof DOMException && error.name === 'AbortError') {
			return;
		}

		console.error(error);
	} finally {
		if (searchControllers.get(form) === controller) {
			searchControllers.delete(form);
			setFormState(form, false);
		}
	}
};

const handleLoadMoreClick = async (event) => {
	event.preventDefault();

	const button = event.currentTarget;
	const wrap = getWrapElement(button);
	const postsContainer = getPostsContainer(button);
	const footer = getFooterElement(postsContainer);
	const currentPage = Number(button.dataset.page || '1');
	const maxPages = Number(button.dataset.max || '1');
	const searchTerm = button.dataset.search || '';

	if (!wrap) {
		return;
	}

	if (currentPage > maxPages) {
		hideButton(button);
		return;
	}

	setButtonState(button, true);

	try {
		const data = await requestPosts(button, currentPage, searchTerm);

		if (data?.html) {
			appendPostsMarkup(wrap, data.html);
		}

		updateFooter(footer, data?.footerText);
		updateButtonPagination(button, data, searchTerm);
	} catch (error) {
		console.error(error);
	} finally {
		setButtonState(button, false);
	}
};

const handleSearchSubmit = async (event) => {
	event.preventDefault();

	const form = event.currentTarget;
	const timerId = searchTimers.get(form);

	if (timerId) {
		window.clearTimeout(timerId);
		searchTimers.delete(form);
	}

	await runSearch(form);
};

const handleSearchInput = (event) => {
	const input = event.currentTarget;
	const form = input instanceof HTMLInputElement ? input.form : null;

	if (!form) {
		return;
	}

	const existingTimerId = searchTimers.get(form);

	if (existingTimerId) {
		window.clearTimeout(existingTimerId);
	}

	const timerId = window.setTimeout(() => {
		searchTimers.delete(form);
		void runSearch(form);
	}, SEARCH_DEBOUNCE_MS);

	searchTimers.set(form, timerId);
};

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll(LOAD_MORE_SELECTOR).forEach((button) => {
		setButtonState(button, false);
		button.addEventListener('click', handleLoadMoreClick);
	});

	document.querySelectorAll(SEARCH_FORM_SELECTOR).forEach((form) => {
		setFormState(form, false);
		form.addEventListener('submit', handleSearchSubmit);

		const searchField = form.querySelector('[name="s"]');

		if (searchField instanceof HTMLInputElement) {
			searchField.addEventListener('input', handleSearchInput);
		}
	});
});
